import React, { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { RedImmobile, RedPagamento, RedDocumento } from '../../../types/red'
import { STATO_PAGAMENTO_CONFIG, EUR } from '../../../constants/red'
import { redApi } from '../../../services/api'
import { downloadBlobResponse } from '../../../lib/downloadBlob'

const TabPagamenti: React.FC<{ immobile: RedImmobile; onUpdate: () => void }> = ({ immobile, onUpdate }) => {
  const [pagamenti, setPagamenti] = useState<RedPagamento[]>([])
  const [loading, setLoading] = useState(true)
  const [form, setForm] = useState({ tipologia: 'caparra', importo: '', data_prevista: '' })
  const [documenti, setDocumenti] = useState<RedDocumento[]>([])
  const [downloadingZip, setDownloadingZip] = useState(false)

  const loadPagamenti = () => {
    redApi.pagamenti.listByImmobile(immobile.id).then(res => {
      setPagamenti(res.data)
      setLoading(false)
    })
  }

  const loadDocumenti = () => {
    redApi.documenti.listByImmobile(immobile.id).then(res => setDocumenti(res.data.filter((d: RedDocumento) => d.caricato))).catch(() => {})
  }

  useEffect(loadPagamenti, [immobile.id])
  useEffect(loadDocumenti, [immobile.id])

  const handleDownloadDocumento = async (doc: RedDocumento) => {
    try {
      const res = await redApi.documenti.download(doc.id)
      downloadBlobResponse(res, doc.titolo)
    } catch {
      toast.error('Errore durante il download del documento')
    }
  }

  const handleDownloadZip = async () => {
    setDownloadingZip(true)
    try {
      const res = await redApi.documenti.downloadZip(documenti.map(d => d.id))
      downloadBlobResponse(res, `${immobile.nome_riferimento || 'documenti'}.zip`)
    } catch {
      toast.error('Errore durante il download dello ZIP')
    } finally {
      setDownloadingZip(false)
    }
  }

  const handleCreate = async () => {
    if (!form.importo) { toast.error('Importo obbligatorio'); return }
    try {
      await redApi.pagamenti.create({
        immobile_id: immobile.id,
        tipologia: form.tipologia,
        importo: parseFloat(form.importo),
        data_prevista: form.data_prevista || null,
      })
      toast.success('Pagamento creato')
      setForm({ tipologia: 'caparra', importo: '', data_prevista: '' })
      loadPagamenti()
      onUpdate()
    } catch {
      toast.error('Errore creazione pagamento')
    }
  }

  const handleMarcaPagato = async (pag: RedPagamento) => {
    const oggi = new Date().toISOString().split('T')[0]
    await redApi.pagamenti.update(pag.id, { data_effettiva: oggi })
    toast.success('Pagamento segnato come pagato')
    loadPagamenti()
    onUpdate()
  }

  if (loading) return <div className="text-gray-400">Caricamento pagamenti...</div>

  return (
    <div>
      <div className="card p-4 mb-6">
        <h2 className="text-sm font-semibold text-gray-800 mb-3">Nuovo pagamento</h2>
        <div className="flex gap-3 items-end flex-wrap">
          <div>
            <label className="label">Tipologia</label>
            <select className="select" value={form.tipologia} onChange={e => setForm({ ...form, tipologia: e.target.value })}>
              <option value="caparra">Caparra</option>
              <option value="saldo_acquisto">Saldo acquisto</option>
              <option value="imposta_registro">Imposta di registro</option>
              <option value="imposta_ipotecaria">Imposta ipotecaria</option>
              <option value="imposta_catastale">Imposta catastale</option>
              <option value="spese_notarili">Spese notarili</option>
              <option value="acconto_appalto">Acconto appalto</option>
              <option value="sal_pagato_imeco">SAL pagato IMECO</option>
              <option value="penale_imeco">Penale IMECO</option>
              <option value="altro">Altro</option>
            </select>
          </div>
          <div>
            <label className="label">Importo (€)</label>
            <input type="number" className="input" value={form.importo} onChange={e => setForm({ ...form, importo: e.target.value })} />
          </div>
          <div>
            <label className="label">Data prevista</label>
            <input type="date" className="input" value={form.data_prevista} onChange={e => setForm({ ...form, data_prevista: e.target.value })} />
          </div>
          <button onClick={handleCreate} className="btn-primary">Aggiungi</button>
        </div>
      </div>

      <div className="space-y-2">
        {pagamenti.map(pag => {
          const cfg = STATO_PAGAMENTO_CONFIG[pag.stato]
          return (
            <div key={pag.id} className="flex items-center justify-between bg-white border border-gray-200 rounded-lg p-3">
              <div>
                <div className="text-sm font-medium text-gray-800">{pag.tipologia.replace(/_/g, ' ')}</div>
                <div className="text-xs text-gray-400">
                  {pag.data_prevista && `Previsto: ${new Date(pag.data_prevista).toLocaleDateString('it-IT')}`}
                  {pag.data_effettiva && ` · Pagato: ${new Date(pag.data_effettiva).toLocaleDateString('it-IT')}`}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="font-semibold">{EUR.format(pag.importo)}</span>
                <span className="text-xs font-medium px-2 py-0.5 rounded-full" style={{ color: cfg.color, backgroundColor: '#f3f4f6' }}>
                  {cfg.label}
                </span>
                {pag.stato !== 'pagato' && (
                  <button onClick={() => handleMarcaPagato(pag)} className="text-xs text-blue-600 hover:underline">
                    Segna pagato
                  </button>
                )}
                <button
                  onClick={async () => {
                    if (!confirm('Eliminare questo pagamento?')) return
                    try {
                      await redApi.pagamenti.delete(pag.id)
                      toast.success('Pagamento eliminato')
                      loadPagamenti()
                      onUpdate()
                    } catch {
                      toast.error('Errore eliminazione')
                    }
                  }}
                  className="text-xs text-red-500 hover:text-red-700"
                  title="Elimina"
                >
                  🗑
                </button>
              </div>
            </div>
          )
        })}
        {pagamenti.length === 0 && (
          <div className="text-center py-10 text-gray-400">Nessun pagamento registrato.</div>
        )}
      </div>

      <div className="card p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-800">Allegati</h2>
          {documenti.length > 0 && (
            <button
              onClick={handleDownloadZip}
              disabled={downloadingZip}
              className="btn-secondary text-sm"
            >
              {downloadingZip ? 'Download...' : `⬇️ Scarica tutto (${documenti.length})`}
            </button>
          )}
        </div>
        {documenti.length === 0 ? (
          <p className="text-sm text-gray-400">Nessun allegato caricato da Anagrafica o Documenti.</p>
        ) : (
          <div className="space-y-2">
            {documenti.map(doc => (
              <div key={doc.id} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2 text-sm">
                <span className="truncate mr-2">{doc.titolo} <span className="text-xs text-gray-400">({doc.tipologia})</span></span>
                <button onClick={() => handleDownloadDocumento(doc)} className="text-blue-600 hover:underline shrink-0">Scarica</button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default TabPagamenti
